'use client';

import Icon from '@ant-design/icons';
import { Modal, Switch, message } from 'antd';
import classNames from 'classnames';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import AddSvg from '@/components/Icon/svgs/add.svg';
import MinusSvg from '@/components/Icon/svgs/mi_remove.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import RouterBackIcon from '@/components/router-back-icon';
import HomeContext from '@/context/HomeContext';
import useAvatarCropper from '@/hooks/useAvatarCropper';
import { useTranslation } from '@/i18n/client';
import {
  EConversationType,
  EGroupRole,
  type ICircleConversation,
  type ICircleSettingDetail,
  type IGroupUserInfo,
  circleReport,
  getCircleSettingDetail,
  quitCircle,
  removeConversationItem,
  updateCircleSetting
} from '@/service/friend';
import useFriendStore from '@/store/useFriendStore';
import { useCircleChatContext } from '@/ui/CircleChat/CircleChatContext';
import { ROUTER_PATH, generatePath } from '@/utils/router';
import { copyToClipboard } from '@/utils/utils';

import { CircleManagementModal } from '../CircleManagementModal';
import { EditDescriptionModal } from './EditDescriptionModal';
import { EditNameModal } from './EditNameModal';
import EditNickNameModal from './EditNickNameModal';
import { InviteMemberModal } from './InviteMemberModal';
import { QRCodeModal } from './QRCodeModal';
import { RemoveMemberModal } from './RemoveMemberModal';
import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

export interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  circleConversation?: ICircleConversation;
}

export const CircleProfileModal = (props: IProps): JSX.Element => {
  const { open, setOpen, circleRoute, circleConversation } = props;
  const { t } = useTranslation();
  const updateFriendCircle = useFriendStore((state) => state.updateFriendCircle);
  const { setCircleDetail: updateCircleDetail } = useCircleChatContext();

  const [loading, setLoading] = useState(false);
  const [circleDetail, setCircleDetail] = useState<ICircleSettingDetail | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  // 二级弹窗状态
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showNickNameModal, setShowNickNameModal] = useState(false);

  // 群管理弹窗状态
  const [showManagementModal, setShowManagementModal] = useState(false);

  // 群二维码弹窗状态
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);

  const { setContext, chatCircleRoute } = useContext(HomeContext);

  // 更新本地会话列表中的群聊信息
  // 目前更新完设置后profileModal就直接刷新一下，所以就先拿刷新后的数据直接更新会话列表的本地数据
  // 不准刷新再说
  const updateLocalCircleConversation = useCallback(
    (detail: ICircleSettingDetail) => {
      if (!circleConversation) return;

      // 构建更新后的会话数据
      const updatedConversation: ICircleConversation = {
        ...circleConversation,
        image: detail.icon,
        title: detail.name,
        muted: detail.muted,
        circle: {
          ...circleConversation.circle,
          memberAvatars: detail.members.slice(0, 4).map((m) => m.avatar)
        }
      };

      updateFriendCircle(updatedConversation);
    },
    [circleConversation, updateFriendCircle]
  );

  // 获取群信息
  const fetchCircleDetail = (shouldUpdateLocal: boolean = false) => {
    if (circleRoute) {
      setLoading(true);
      getCircleSettingDetail({ route: circleRoute })
        .then((res) => {
          if (res.data.code === 1) {
            message.error(res.data.message || t('error.oops'));
            handleClose();
          }

          if (res.data.code === 0) {
            const detail = res.data.data;

            setCircleDetail(detail);

            updateCircleDetail(detail);

            // 如果需要更新本地会话列表
            if (shouldUpdateLocal) {
              updateLocalCircleConversation(detail);
            }

            return;
          }
        })
        .catch((err) => {
          console.error('Failed to get circle detail:', err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (open && circleRoute) {
      fetchCircleDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, circleRoute]);

  useEffect(() => {
    if (circleDetail) {
      setIsMuted(!!circleDetail.muted);
    }
  }, [circleDetail]);

  // 处理成员显示逻辑
  const displayMembers = useMemo(() => {
    if (!circleDetail?.members) {
      return [];
    }

    return circleDetail.members;
  }, [circleDetail?.members]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleCancel = useCallback(
    (event?: ReactMouseEvent<HTMLElement>) => {
      event?.stopPropagation();
      handleClose();
    },
    [handleClose]
  );

  // 二级弹窗操作成功的回调
  const handleSubModalSuccess = useCallback(
    (detail?: ICircleSettingDetail) => {
      if (!detail) return;

      setCircleDetail(detail);
      updateCircleDetail(detail);
      updateLocalCircleConversation(detail);
    },
    [updateCircleDetail, updateLocalCircleConversation]
  );

  // 打开邀请好友弹窗
  const handleInvite = () => {
    setShowInviteModal(true);
  };

  const handleExit = () => {
    // 先退出群聊
    quitCircle({ route: circleRoute })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        // 退出成功后删除会话
        return removeConversationItem({
          type: EConversationType.CIRCLE,
          uniqueKey: circleRoute
        });
      })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message);
        }

        message.success(t('circle.exitSuccess'));

        // 本地更新删除
        if (circleConversation) {
          updateFriendCircle(circleConversation, true);
        }

        setOpen(false);

        if (circleRoute === chatCircleRoute) {
          setContext({ chatCircleRoute: undefined });
        }
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  // 打开群管理弹窗
  const handleOpenManagement = () => {
    setShowManagementModal(true);
  };

  // 分享群邀请
  const handleShareInvite = () => {
    const shareUrl = `${window.origin}${generatePath(ROUTER_PATH.group, { route: circleRoute })}`;

    copyToClipboard(shareUrl).then(() => {
      message.success(
        <span
          dangerouslySetInnerHTML={{
            __html: t('memoryDetail.shareModal.copySuccessTips', {
              url: shareUrl
            })
          }}
        />
      );
    });
  };

  // 打开群二维码弹窗
  const handleOpenQRCode = () => {
    setShowQRCodeModal(true);
  };

  // 打开移除成员弹窗
  const handleRemove = () => {
    setShowRemoveModal(true);
  };

  // 判断是否有编辑权限
  const canEdit =
    circleDetail?.role === EGroupRole.OWNER || circleDetail?.role === EGroupRole.ADMIN;

  // 头像上传 hook
  const { FileInput, CropperModal } = useAvatarCropper({
    handleSetAvatarCb: (avatar) => {
      // 上传成功后更新群头像
      updateCircleSetting({
        route: circleRoute,
        icon: avatar
      })
        .then((res) => {
          if (res.data.code !== 0) {
            throw new Error(res.data.message || t('error.oops'));
          }

          message.success(t('circle.updateSuccess'));
          handleSubModalSuccess({
            ...circleDetail!,
            icon: avatar
          });
        })
        .catch((e) => {
          message.error(e.message);
        });
    },
    cropperModalTitle: t('circle.uploadAvatar'),
    zIndex: 1100
  });

  // 打开名称编辑弹窗
  const handleOpenNameEdit = () => {
    if (!canEdit) return;

    setShowNameModal(true);
  };

  // 打开群介绍编辑弹窗
  const handleOpenDescriptionEdit = () => {
    if (!canEdit) return;

    setShowDescriptionModal(true);
  };

  const handleReport = () => {
    circleReport({ route: circleRoute })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('homepage.settingModal.reportSuccess'));
      })
      .catch((err) => {
        message.error(err.message);
      });
  };

  // 切换消息免打扰
  const handleToggleMuteNotifications = (checked: boolean) => {
    const prevMuted = isMuted;

    setIsMuted(checked);
    updateCircleSetting({
      route: circleRoute,
      muted: checked
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('circle.updateSuccess'));
        handleSubModalSuccess({
          ...circleDetail!,
          muted: checked
        });
      })
      .catch((e) => {
        setIsMuted(prevMuted);
        message.error(e.message);
      });
  };

  // 打开nickname编辑弹窗
  const handleOpenNickNameEdit = () => {
    setShowNickNameModal(true);
  };

  const renderMemberItem = (member: IGroupUserInfo) => (
    <div key={member.id} className={styles.memberItem}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img alt={member.username} className={styles.memberAvatar} src={member.avatar} />
      <span className={styles.memberName}>{member.nickname || member.username}</span>
    </div>
  );

  const renderInviteItem = () => (
    <div
      key="invite"
      className={classNames(styles.memberItem, styles.inviteItem)}
      onClick={handleInvite}
    >
      <div className={styles.inviteAvatar}>
        <Icon className={styles.addIcon} component={AddSvg} />
      </div>
      <span className={styles.memberName}>{t('circle.invite')}</span>
    </div>
  );

  const renderRemoveItem = () => (
    <div
      key="remove"
      className={classNames(styles.memberItem, styles.inviteItem)}
      onClick={handleRemove}
    >
      <div className={styles.inviteAvatar}>
        <Icon className={styles.removeIcon} component={MinusSvg} />
      </div>
      <span className={styles.memberName}>{t('circle.remove')}</span>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loadingContainer}>
          <LottieJumpLoading className="!w-12 !h-12" />
        </div>
      );
    }

    if (!circleDetail) {
      return null;
    }

    return (
      <div className={styles.contentContainer}>
        {/* 成员区块 */}
        <div className={styles.membersCard}>
          {displayMembers.map(renderMemberItem)}
          {/* 邀请按钮 */}
          {renderInviteItem()}
          {/* 移除按钮（群主和管理员可见） */}
          {(circleDetail.role === EGroupRole.OWNER || circleDetail.role === EGroupRole.ADMIN) &&
            renderRemoveItem()}
        </div>

        {/* 群信息卡片 */}
        <div className={styles.infoCard}>
          {/* 群头像 */}
          <label
            className={classNames(styles.infoRow, { [styles.editable]: canEdit })}
            // onClick={handleOpenAvatarEdit}
          >
            <span className={styles.infoLabel}>{t('circle.avatar')}</span>
            <div className={styles.infoValue}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt="avatar"
                className={styles.avatarIcon}
                src={
                  circleDetail.icon ||
                  'https://mindverseglobal.mindverse.com/front-img/crm/1766814853894/defaultImage.png'
                }
              />
              {canEdit && <Icon className={styles.arrowIcon} component={RightSvg} />}
            </div>
            {canEdit && FileInput}
          </label>

          {/* 群名称 */}
          <div
            className={classNames(styles.infoRow, { [styles.editable]: canEdit })}
            onClick={handleOpenNameEdit}
          >
            <span className={styles.infoLabel}>{t('circle.name')}</span>
            <div className={styles.infoValue}>
              <span className={styles.nameText}>{circleDetail.name}</span>
              {canEdit && <Icon className={styles.arrowIcon} component={RightSvg} />}
            </div>
          </div>

          {/* 群介绍 */}
          <div
            className={classNames(styles.infoRow, { [styles.editable]: canEdit })}
            onClick={handleOpenDescriptionEdit}
          >
            <span className={styles.infoLabel}>{t('circle.description')}</span>
            <div className={styles.infoValue}>
              {circleDetail.description ? (
                <span className={styles.descriptionText}>{circleDetail.description}</span>
              ) : (
                <span className={styles.notSetText}>{t('common.notSet')}</span>
              )}
              {canEdit && <Icon className={styles.arrowIcon} component={RightSvg} />}
            </div>
          </div>

          {/* 群管理（群主和管理员可见） */}
          {(circleDetail.role === EGroupRole.OWNER || circleDetail.role === EGroupRole.ADMIN) && (
            <div className={styles.infoRow} onClick={handleOpenManagement}>
              <span className={styles.infoLabel}>{t('circle.management')}</span>
              <div className={styles.infoValue}>
                {circleDetail.adminSettings.pendingApplyCount > 0 && (
                  <span className={styles.pendingCount}>
                    {circleDetail.adminSettings.pendingApplyCount}
                  </span>
                )}
                <Icon className={styles.arrowIcon} component={RightSvg} />
              </div>
            </div>
          )}
        </div>

        {/* 邀请分享卡片 */}
        <div className={styles.inviteInfoCard}>
          {/* 分享群邀请 */}
          <div className={styles.inviteRow} onClick={handleShareInvite}>
            <span className={styles.inviteLabel}>{t('circle.shareInvite')}</span>
            <div className={styles.inviteValue}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt="share"
                className={styles.shareIcon}
                src="https://mindverseglobal.mindverse.com/front-img/crm/1768445735256/Vector.png"
              />
              <Icon className={styles.arrowIcon} component={RightSvg} />
            </div>
          </div>

          {/* 群二维码 */}
          <div className={styles.inviteRow} onClick={handleOpenQRCode}>
            <span className={styles.inviteLabel}>{t('circle.qrCode')}</span>
            <div className={styles.inviteValue}>
              <img
                alt="share"
                className={styles.shareIcon}
                src="https://mindverseglobal.mindverse.com/front-img/crm/1768559341573/qrcode.svg"
              />
              <Icon className={styles.arrowIcon} component={RightSvg} />
            </div>
          </div>
        </div>

        {/* 消息免打扰 */}
        <div className={styles.muteNotifications}>
          <span className={styles.rowLabel}>{t('circle.muteNotifications')}</span>
          <div className={styles.rowRight}>
            <Switch checked={isMuted} onChange={handleToggleMuteNotifications} size="small" />
          </div>
        </div>

        {/* 群nickname */}
        <div className={styles.nicknameCard} onClick={handleOpenNickNameEdit}>
          <span className={styles.rowLabel}>{t('circle.editNickname')}</span>
          <div className={styles.rowRight}>
            <span className={styles.nicknameText}>
              {circleDetail.nickname || t('common.notSet')}
            </span>
            <Icon className={styles.arrowIcon} component={RightSvg} />
          </div>
        </div>

        {/* 底部操作区 */}
        <div className={styles.actionContainer}>
          {circleDetail.role !== EGroupRole.OWNER && (
            <div className={styles.exitBtn} onClick={handleExit}>
              {t('circle.exitAndDelete')}
            </div>
          )}
          <div className={styles.reportBtn} onClick={() => handleReport()}>
            {t('circle.report')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        className={styles.circleProfileModal}
        closable={false}
        destroyOnClose
        footer={null}
        modalRender={stopPropagationModalRender}
        onCancel={handleCancel}
        open={open}
      >
        <div className={styles.container}>
          {/* 头部 */}
          <div className={styles.headerContainer}>
            <div className={styles.headerLeft}>
              <RouterBackIcon handleClick={handleClose} />
              <span className={styles.title}>
                {t('circle.info')} {circleDetail ? `(${circleDetail.memberCount})` : ''}
              </span>
            </div>
            <div className={styles.headerRight} />
          </div>

          {renderContent()}
        </div>
      </Modal>

      {/* 群二维码弹窗 */}
      <QRCodeModal
        circleIcon={circleDetail?.icon}
        circleName={circleDetail?.name}
        circleRoute={circleRoute}
        open={showQRCodeModal}
        setOpen={setShowQRCodeModal}
      />

      {/* 群管理弹窗 */}
      {(circleDetail?.role === EGroupRole.OWNER || circleDetail?.role === EGroupRole.ADMIN) && (
        <CircleManagementModal
          circleConversation={circleConversation}
          circleDetail={circleDetail}
          circleRoute={circleRoute}
          handleUpdate={fetchCircleDetail}
          open={showManagementModal}
          setOpen={setShowManagementModal}
        />
      )}

      {/* 邀请成员弹窗 */}
      <InviteMemberModal
        circleRoute={circleRoute}
        onSuccess={handleSubModalSuccess}
        open={showInviteModal}
        setOpen={setShowInviteModal}
      />

      {/* 移除成员弹窗 */}
      <RemoveMemberModal
        circleRoute={circleRoute}
        onSuccess={handleSubModalSuccess}
        open={showRemoveModal}
        setOpen={setShowRemoveModal}
      />

      {/* 头像上传 - 使用 label 包装以便通过 id 触发 */}
      {CropperModal}

      {/* 编辑群名称弹窗 */}
      <EditNameModal
        circleRoute={circleRoute}
        currentDetail={circleDetail}
        initialName={circleDetail?.name || ''}
        onSuccess={handleSubModalSuccess}
        open={showNameModal}
        setOpen={setShowNameModal}
      />

      {/* 编辑群昵称弹窗 */}
      <EditNickNameModal
        circleRoute={circleRoute}
        currentDetail={circleDetail}
        initialName={circleDetail?.nickname || ''}
        onSuccess={handleSubModalSuccess}
        open={showNickNameModal}
        setOpen={setShowNickNameModal}
      />

      {/* 编辑群介绍弹窗 */}
      <EditDescriptionModal
        circleRoute={circleRoute}
        currentDetail={circleDetail}
        initialDescription={circleDetail?.description || ''}
        onSuccess={handleSubModalSuccess}
        open={showDescriptionModal}
        setOpen={setShowDescriptionModal}
      />
    </>
  );
};

export default CircleProfileModal;
